import type Highcharts from "highcharts";
import * as io from "io-ts";
import mergeDeep from "lodash.merge";
import React from "react";
import type { ActiveAnalysis } from "../../../../../api/useActiveAnalysesStream";
import type { Project } from "../../../../../api/useProjects";
import type { Setup } from "../../../../../api/useSetups";
import t from "../../../../../i18n";
import { comparator, comparatorBy, withNull } from "diagnostics-lib/comparator";
import { color } from "@ui";
import type { Options } from "@ui/Highcharts";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import type { SelectedQuantity } from "../../../useSelectedQuantities";
import useSelectedQuantities from "../../../useSelectedQuantities";
import { QUANTITY_GAP_SERIE_ID } from "../getQuantityGapsSerie";
import { useCommonOptions } from "../hook/useCommonOptions";
import type { Observable, Quantity } from "../../../../../api/useQuantities";
import type { Event } from "../../../../../api/useEvents";
import {
  FAULT_IMPACT_SERIE,
  MANUAL_FAILURE_IMPACT_SERIE,
  QUANTITY_UNEXPLAINED_GAP_ID,
} from "../../../Chart/series/serieIds";

export const ObservableOrder = io.keyof({
  ALPHABETICAL_ORDER: null,
  DECREASING_IMPACT_ORDER: null,
});

export type ObservableOrder = io.TypeOf<typeof ObservableOrder>;

export function isObservable(quantity: Quantity, setup: Setup | null) {
  return setup ? Boolean(setup?.simulationOutputs.find(q => q.quantityId === quantity.id)) : false;
}

interface CategoryData {
  name: string;
  value: number;
}

const compareByName = comparatorBy<
  string | number | undefined,
  "name",
  Highcharts.PointOptionsObject
>("name", withNull(comparator));

function compareByValue(a: CategoryData, b: CategoryData): number {
  return b.value - a.value;
}

function generateCategories(
  series: Highcharts.SeriesColumnOptions[],
  order: ObservableOrder,
  selectedQuantities: SelectedQuantity[],
  flagSelectedQuantities: boolean,
  setup: Setup | null,
  event?: Event,
) {
  const compareFunction = order === "ALPHABETICAL_ORDER" ? compareByName : compareByValue;
  const symptomSerie = series.find(s => s.id === QUANTITY_GAP_SERIE_ID);
  const unexplainedGapSerie = series.find(s => s.id === QUANTITY_UNEXPLAINED_GAP_ID);

  if (!symptomSerie) throw new Error("Missing serie");
  const symptomValues = (symptomSerie.data as Highcharts.PointOptionsObject[]).sort(compareByName);
  const unexplainedGapValues = unexplainedGapSerie
    ? (unexplainedGapSerie.data as Highcharts.PointOptionsObject[]).sort(compareByName)
    : null;

  const visibleSeries = series.filter(s => s.visible);
  const impactSeries = visibleSeries.filter(
    s => s.id !== QUANTITY_GAP_SERIE_ID && s.id !== QUANTITY_UNEXPLAINED_GAP_ID,
  );

  const failureImpactSerieEvent =
    event &&
    visibleSeries.find(serie => {
      switch (event.type) {
        case "DIAGNOSED":
          return event.metadata.faults.some(
            fault => serie.id === `${FAULT_IMPACT_SERIE}-${fault.name}`,
          );

        case "MANUAL":
          return serie.id === `${MANUAL_FAILURE_IMPACT_SERIE}-${event.metadata.manualFaultName}`;

        default:
          return false;
      }
    });

  const index = impactSeries.map(s => {
    const data = s.data as Highcharts.PointOptionsObject[];

    return data.reduce<{ [quantityName: string]: number }>((acc, d) => {
      acc[String(d.name)] = d.y ?? 0;
      return acc;
    }, {});
  });
  const sumImpactValues = symptomValues.map(point =>
    index.reduce((acc, map) => acc + (map[String(point.name)] ?? 0), 0),
  );

  const categoryData = symptomValues.map((d, i) => {
    const unexplainedGap = unexplainedGapValues === null ? null : (unexplainedGapValues[i] ?? null);
    const values = [sumImpactValues[i] ?? 0];

    if (symptomSerie.visible) values.push(d.y ?? 0);
    if (unexplainedGapSerie !== undefined && unexplainedGapSerie.visible && unexplainedGap !== null)
      values.push(unexplainedGap.y ?? 0);

    return {
      name: String(d.name),
      value: Math.max(...values.map(Math.abs)),
    };
  });

  const sortedCategories =
    failureImpactSerieEvent?.data && order === "DECREASING_IMPACT_ORDER"
      ? (failureImpactSerieEvent.data as Highcharts.PointOptionsObject[])
          .slice()
          .sort((a, b) => Math.abs(b.y ?? 0) - Math.abs(a.y ?? 0))
          .map(dataPoint => String(dataPoint.name))
      : categoryData.sort(compareFunction).map(d => d.name);

  const selectedObservableNames = selectedQuantities
    .filter(q => isObservable(q.quantity, setup) && sortedCategories.includes(q.quantity.name))
    .map(s => s.quantity.name);

  const otherNames = sortedCategories.filter(
    category => !selectedObservableNames.includes(category),
  );

  return flagSelectedQuantities ? selectedObservableNames.concat(otherNames) : sortedCategories;
}

function useOptions({
  visibleSymptomHistogramChartId,
  series,
  analysis,
  setups,
  quantities,
  order,
  project,
  flagSelectedQuantities,
  event,
  observables,
}: {
  visibleSymptomHistogramChartId: string;
  series: Highcharts.SeriesColumnOptions[];
  analysis: ActiveAnalysis;
  setups: Setup[];
  quantities: Quantity[];
  order: ObservableOrder;
  project: Project;
  showInactiveObservables: boolean;
  flagSelectedQuantities: boolean;
  event?: Event;
  observables: Observable[];
}): Options {
  const [selectedQuantities] = useSelectedQuantities(project.id);
  const commonOptions = useCommonOptions({
    visibleSymptomHistogramChartId,
    series,
    analysis,
    quantities,
    setups,
    project,
    flagSelectedQuantities,
    observables,
  });
  const setup = React.useMemo(() => getAnalysisSetup(setups, analysis), [setups, analysis]);

  return React.useMemo(() => {
    if (!commonOptions.series) throw new Error("Option should have series");
    const categories = generateCategories(
      commonOptions.series as Highcharts.SeriesColumnOptions[],
      order,
      selectedQuantities,
      flagSelectedQuantities,
      setup,
      event,
    );
    const options = mergeDeep(
      {
        chart: {
          zoomType: "y",
          marginLeft: 80,
          marginRight: 50,
          marginBottom: 10,
          scrollablePlotArea: { minWidth: categories.length * 75 },
        },
        xAxis: { categories },
        yAxis: {
          title: {
            text: t("diagnosis.charts.symptoms.observable.axis.y"),
          },
          labels: {
            sign: true,
            unit: "σ",
            noSpaceBetweenValueAndUnit: true,
          },
          plotBands: [
            {
              from: 2,
              to: -2,
              color: color.chlorine100,
            },
          ],
        },
      },
      commonOptions,
    );
    return options;
  }, [commonOptions, order, selectedQuantities, flagSelectedQuantities, setup, event]);
}

export default useOptions;
